import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MessageSquare, Mail, MailOpen } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * MessagesBadge Component
 * Displays an unread message count badge for managers/admins
 * Polls the messaging API for updates
 */
const MessagesBadge = ({ className }) => {
    const { api, user, hasPermission } = useAuth();
    const navigate = useNavigate();
    const [unreadCount, setUnreadCount] = useState(0);
    
    // Only show for roles that can receive messages
    const canView = user?.role && ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user.role);
    
    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        if (!canView) return;
        try {
            const res = await api().get('/messaging/unread-count');
            setUnreadCount(res.data?.count || res.data?.unread_messages || 0);
        } catch (error) {
            console.error('Error fetching message count:', error);
        }
    }, [api, canView]);
    
    // Initial fetch and polling
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30 seconds
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);
    
    if (!canView) return null;
    
    return (
        <Button
            variant="ghost"
            size="sm"
            className={cn("relative", className)}
            onClick={() => navigate('/messaging')}
            data-testid="messages-badge-btn"
            title="Messages"
        >
            {unreadCount > 0 ? (
                <Mail className="w-5 h-5 text-blue-600" />
            ) : (
                <MailOpen className="w-5 h-5 text-gray-400" />
            )}
            {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                </span>
            )}
        </Button>
    );
};

export default MessagesBadge;
